import { faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useMediaQuery from '@readcloud/common/lib/useMediaQuery';
import cn from 'classnames';

import {
  Button,
  DialogContent,
  Dialog,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@readcloud/ui';
import { Cloud, User } from '@readcloud/data';
import { getSubdomainAssets } from '@readcloud/common/lib/utils/utils';
import * as React from 'react';
import { DeleteCloudOperation, UpdateCloudOperation } from '../types';
import { ReactComponent as CloudIcon } from './Cloud.svg';
import { UpdateCloudFormModal } from './CloudFormModal';
import { MdEdit, MdAdd, MdExitToApp, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { RiDragDropLine } from 'react-icons/ri';
import { trackEvent } from '@readcloud/common';
import { UploadWizardDialog } from './upload/UploadWizardDialog';
import { Toggle } from '@readcloud/ui/toggle';
import { Settings, X } from 'lucide-react';
import { useCloudsAndContentActions } from '../context/ActionsContext';

interface HeadingProps {
  cloud: Cloud;
  currentUserId: string;
  moduleAllowed?: string[];
  onUpdateCloud: UpdateCloudOperation;
  onDeleteCloud: DeleteCloudOperation;
  knownUsers: User[];
  onRefresh: () => void;
  canUpdateCloud: boolean;
  canDeleteCloud: boolean;
  canAddContent: boolean;
  toggleArrangingContent: (event: React.MouseEvent<HTMLElement>) => void;
  arrangingCloudId?: string;
  toggleVisibleMyContent?: ()=>void;
  myContentVisible?: boolean;
}
export const CloudHeading: React.FC<HeadingProps> = (props: HeadingProps) => {
  const [editing, setEditing] = React.useState<boolean>(false);  

  const [openUploadDialog, setOpenUploadDialog] =
    React.useState<boolean>(false);

  const isScreenGreaterThanSM = useMediaQuery('(min-width: 768px)');

  function renderArrangeContentButton() {
    return (
      <Toggle
        pressed={props.arrangingCloudId === props.cloud.id}
        variant="ghost"
        className={cn('rc-gap-2 rc-w-full md:rc-w-auto rc-justify-start', !isScreenGreaterThanSM && 'rc-px-4', props.cloud.id == props.arrangingCloudId && 'rc-w-fit' )}
        onClick={(event) => props.toggleArrangingContent(event)}
      >
        {props.cloud.id !== props.arrangingCloudId ? (
          <>
            <RiDragDropLine />
            <span>Reorder</span>
          </>
        ) : (
          <>
            <MdExitToApp />
            <span>Exit</span>
          </>
        )}
      </Toggle>
    );
  }

  function renderAddBooksButton() {
    const isArranging = !!props.arrangingCloudId;
    return (
      <Button
        variant="ghost"
        className={cn(
          'rc-gap-2',
          !isScreenGreaterThanSM && 'rc-px-4',
          isArranging && 'rc-opacity-20'
        )}
        onClick={() => setOpenUploadDialog(true)}
      >
        <MdAdd />
        <span>Add Content</span>
      </Button>
    );
  }

  function renderEditCloudButton() {
    const isArranging = !!props.arrangingCloudId;
    return (
      <Button
        variant="ghost"
        onClick={() => {
          trackEvent('Webapp/CloudEditClick');
          setEditing(true);
        }}
        className={cn('rc-gap-2 rc-w-full md:rc-w-auto md:rc-justify-center rc-justify-start', isArranging && 'rc-opacity-20')}
      >
        <MdEdit />
        <span>Edit</span>
      </Button>
    );
  }

  function renderHideShowButton() {
    return (
      <Button
        variant="ghost"
        className={cn('rc-gap-2')}
        onClick={() => props.toggleVisibleMyContent?.()}
      >
        {props.myContentVisible ? <MdVisibility /> : <MdVisibilityOff />}
        <small>{props.myContentVisible ? 'Show All' : 'Hide items in other clouds'}</small>
      </Button>
    );
  }

  const {name} = getSubdomainAssets();

  const nameMap = {
    'My Purchases': name.MY_PURCHASES,
    'My Interactive Logins': name.MY_INTERACTIVE_LOGINS,
    'My Content': name.MY_CONTENTS
  };
  

  return (
    <div>
      <div className="rc-flex rc-py-4 rc-px-6 rc-gap-2 rc-items-end readcloud-ui readcloud-ui-reset rc-w-full">
        <div style={{
          flex: 1,
          display: 'flex',

          flexDirection: 'column',
          gap: '0.5rem',
        }}>
          <div
            className="rc-text-primary"
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'flex-end',
              gap: '0.5rem',
            }}
          >
            {props.cloud.id === 'my-uploads' ? (
              <Settings
                style={{ width: '2em', height: 'auto', strokeWidth: 1 }}
              />
            ) : (
              <CloudIcon style={{ width: '2em', height: 'auto' }} />
            )}
            <h3
              data-testid="cloud-name"
              style={{
                margin: 0,
                fontSize: '1.1em',
              }}
            >
              {nameMap[props.cloud.name] || props.cloud.name}
            </h3>
          </div>
          {(
            nameMap[props.cloud.name] === nameMap["My Purchases"] ||
            nameMap[props.cloud.name] === nameMap["My Content"]
          ) &&
            <small className="rc-flex rc-gap-2 rc-text-muted-foreground rc-items-center">
              {
                props.myContentVisible ? 
                <span>Hiding items in other clouds</span>
                :
                <span>Showing all {props.cloud.id === "my-content" ? "content" : "purchases"}</span>
              }
              {(
                nameMap[props.cloud.name] === nameMap["My Purchases"] ||
                nameMap[props.cloud.name] === nameMap["My Content"]
              ) &&
                renderHideShowButton()
              }
            </small>
          }
        </div>

        {props.canUpdateCloud &&
          isScreenGreaterThanSM &&
          props.cloud.id !== props.arrangingCloudId &&
          renderEditCloudButton()}
        {props.canAddContent &&
          isScreenGreaterThanSM &&
          props.cloud.id !== props.arrangingCloudId &&
          renderAddBooksButton()}
        {props.canAddContent &&
          (isScreenGreaterThanSM ||
            props.arrangingCloudId === props.cloud.id) &&
          renderArrangeContentButton()}
        {!isScreenGreaterThanSM &&
          (props.canUpdateCloud || props.canAddContent) &&
          props.cloud.id !== props.arrangingCloudId && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost">
                  <FontAwesomeIcon size="lg" icon={faEllipsisVertical} />
                </Button>
              </PopoverTrigger>

              <PopoverContent
                className="readcloud-ui readcloud-ui-reset bg-light p-2 rounded"
                collisionPadding={16}
                align="end"
                style={{width: "fit-content"}}
              >
                <div className="rc-flex rc-flex-col rc-items-start">
                  {props.canUpdateCloud && renderEditCloudButton()}
                  {props.canAddContent && renderAddBooksButton()}
                  {props.canAddContent && renderArrangeContentButton()}
                </div>
              </PopoverContent>
            </Popover>
          )}
      </div>
      {props.canUpdateCloud && (
        <UpdateCloudFormModal
          isOpen={editing}
          currentUserId={props.currentUserId}
          previousCloud={props.cloud}
          knownUsers={props.knownUsers}
          onCancel={() => setEditing(false)}
          onUpdateCloud={async (cloud) => {
            const result = await props.onUpdateCloud(cloud);
            setEditing(false);
            return result;
          }}
          canDeleteCloud={props.canDeleteCloud}
          onDeleteCloud={props.onDeleteCloud}
        />
      )}
      {
        <UploadWizardDialog
          open={openUploadDialog}
          onOpenChange={setOpenUploadDialog}
          cloudId={props.cloud.id}
        />
      }
      {/* {addingBook && props.canAddContent && (
        <AddContentModal
          cloudId={props.cloud.id}
          open
          onCancel={() => setAddingBook(false)}
          onComplete={() => {
            setAddingBook(false);
            props.onRefresh();
            // TODO: Scroll and outline/highlight the new book (to show the user where it is, somehow)
          }}
        />
      )} */}
    </div>
  );
};
